import { type ReactNode, useMemo, useState } from "react";

import {
  Box,
  CloseIcon,
  FormField,
  IconButton,
  MeetingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  TagIcon,
  Text,
  TextWithTooltip,
  WarningIcon,
} from "@hightouch/htui";
import { format, isSameDay } from "date-fns";
import { Controller } from "react-hook-form";

import type { AmpTaskRowFragment } from "src/graphql";
import { isPendingId } from "src/pages/projects/board-cache";
import { useBoard } from "src/pages/projects/board-context";
import { TASK_PEOPLE_LABELS } from "src/pages/projects/constants";
import {
  ARCHIVED,
  endOfDayIso,
  isOverdue,
  resolveBoardColor,
  SUBTASK_CATEGORIES,
  STATUS_CATEGORY_LABELS,
  sortStatuses,
  startOfDayIso,
  TASK_STATE_LABELS,
  taskCategory,
} from "src/pages/projects/utils";
import { Calendar } from "src/ui/calendar";
import { DateTimeSelect } from "src/ui/datetime-select/datetime-select";

import { PeoplePicker } from "./people";
import {
  type Appearance,
  PropertyButton,
  PropertyPicker,
} from "./property-picker";
import {
  CATEGORY_ICONS,
  ColorDot,
  colorDotIcon,
  StatusIcon,
} from "./status-icon";
import { StatusMenu, type StatusMenuOption } from "./status-menu";

type StatusPickerProps = {
  task: Pick<
    AmpTaskRowFragment,
    | "id"
    | "status_id"
    | "status"
    | "status_category"
    | "parent_task_id"
    | "deleted_at"
  >;
  appearance?: Appearance;
  /** Hides the status name, for dense rows that show only the icon. */
  iconOnly?: boolean;
};

/** A task's workflow status, or a subtask's fixed one. */
export const StatusPicker = (props: StatusPickerProps) =>
  props.task.parent_task_id ? (
    <SubtaskStatusPicker {...props} />
  ) : (
    <WorkflowStatusPicker {...props} />
  );

// A subtask's statuses are the same everywhere, so their menu options are too.
const SUBTASK_STATUS_OPTIONS: StatusMenuOption[] = SUBTASK_CATEGORIES.map(
  (category) => ({
    value: category,
    label: STATUS_CATEGORY_LABELS[category],
    icon: CATEGORY_ICONS[category],
  }),
);

const SubtaskStatusPicker = ({
  task,
  appearance = "pill",
  iconOnly = false,
}: StatusPickerProps) => {
  const { tasks } = useBoard();
  const current = taskCategory(task);

  return (
    <StatusMenu
      options={SUBTASK_STATUS_OPTIONS}
      value={current}
      appearance={appearance}
      iconOnly={iconOnly}
      // Some stored subtasks sit in review, which the menu doesn't offer, so name their category rather than "No status".
      fallback={{
        label: STATUS_CATEGORY_LABELS[current],
        icon: CATEGORY_ICONS[current],
      }}
      onChange={(category) => {
        const next = SUBTASK_CATEGORIES.find((value) => value === category);
        if (next) tasks.setSubtaskStatus(task.id, next);
      }}
    />
  );
};

const ARCHIVED_OPTION: StatusMenuOption = {
  value: ARCHIVED,
  label: TASK_STATE_LABELS.archived,
  icon: CATEGORY_ICONS[ARCHIVED],
  isSeparate: true,
};

const WorkflowStatusPicker = ({
  task,
  appearance = "pill",
  iconOnly = false,
}: StatusPickerProps) => {
  const { statusesFor, tasks } = useBoard();
  const statuses = statusesFor(task.status?.workflow_id);

  // Menu items take an icon component, one per status; archiving is open to every task after them.
  const options = useMemo<StatusMenuOption[]>(
    () => [
      ...sortStatuses(statuses).map((status) => ({
        value: status.id,
        label: status.name,
        icon: () => <StatusIcon status={status} />,
      })),
      ARCHIVED_OPTION,
    ],
    [statuses],
  );

  return (
    <StatusMenu
      options={options}
      value={task.deleted_at ? ARCHIVED : (task.status_id ?? "")}
      appearance={appearance}
      iconOnly={iconOnly}
      fallback={
        task.status
          ? {
              label: task.status.name,
              icon: () => <StatusIcon status={task.status} />,
            }
          : undefined
      }
      onChange={(value) => {
        const status = statuses.find((candidate) => candidate.id === value);
        if (value === ARCHIVED) {
          tasks.setArchived(task.id, true);
        } else if (status) {
          tasks.setStatus(task, status);
        }
      }}
    />
  );
};

type TaskPeoplePickerProps = {
  task: Pick<AmpTaskRowFragment, "id" | "owners" | "assignees">;
  appearance?: Appearance;
  avatarsOnly?: boolean;
};

/** The one person responsible for a task. */
export const OwnerPicker = (props: TaskPeoplePickerProps) => {
  const { tasks } = useBoard();
  return (
    <PeoplePicker
      {...TASK_PEOPLE_LABELS.owner}
      people={props.task.owners}
      isSingle
      appearance={props.appearance}
      avatarsOnly={props.avatarsOnly}
      onChange={(userIds) => tasks.setPeople(props.task.id, "owner", userIds)}
    />
  );
};

/** The people and groups doing a task, who change as it moves from hand to hand. */
export const AssigneesPicker = (props: TaskPeoplePickerProps) => {
  const { tasks } = useBoard();
  return (
    <PeoplePicker
      {...TASK_PEOPLE_LABELS.assignee}
      people={props.task.assignees}
      appearance={props.appearance}
      avatarsOnly={props.avatarsOnly}
      onChange={(userIds) =>
        tasks.setPeople(props.task.id, "assignee", userIds)
      }
      onGroupsChange={(userGroupIds) =>
        tasks.setAssigneeGroups(props.task.id, userGroupIds)
      }
    />
  );
};

type DueDatePickerProps = {
  task: Pick<
    AmpTaskRowFragment,
    "id" | "due_at" | "status" | "status_category" | "deleted_at"
  >;
  appearance?: Appearance;
  /** Hides the picker when no date is set, for dense rows. */
  hideWhenEmpty?: boolean;
  textSize?: DatePickerButtonProps["textSize"];
};

export const DueDatePicker = ({
  task,
  appearance = "pill",
  hideWhenEmpty = false,
  textSize,
}: DueDatePickerProps) => {
  const { tasks } = useBoard();

  if (hideWhenEmpty && !task.due_at) return null;

  return (
    <DatePickerButton
      value={task.due_at}
      appearance={appearance}
      textSize={textSize}
      isOverdue={isOverdue(task)}
      onChange={(date) =>
        tasks.setDueDate(task.id, date ? endOfDayIso(date) : null)
      }
    />
  );
};

/** A date button's icon and date, which sit closer on a card or row than in a form. */
const DateContent = ({
  isCompact,
  children,
}: {
  isCompact: boolean;
  children: ReactNode;
}) =>
  isCompact ? (
    <Row align="center" gap={0.5}>
      {children}
    </Row>
  ) : (
    <>{children}</>
  );

type DatePickerButtonProps = {
  /** An ISO instant, or null for no date. */
  value: string | null;
  appearance?: Appearance;
  /** "sm" sets the date in small secondary text, as task cards and rows show it. */
  textSize?: "sm" | "md";
  isOverdue?: boolean;
  label?: string;
  /** The first and last days the calendar offers. */
  minDate?: Date;
  maxDate?: Date;
  /** Replaces the icon and date on the button, for a control that sets a date rather than showing one. */
  children?: ReactNode;
  /** The day picked, or null when cleared; not called for the day already set. */
  onChange: (day: Date | null) => void;
};

/** A date control: the date as a property button, a calendar on click. */
export const DatePickerButton = ({
  value,
  appearance = "pill",
  textSize = "md",
  isOverdue = false,
  label = "Due date",
  minDate,
  maxDate,
  children,
  onChange,
}: DatePickerButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const date = value ? new Date(value) : undefined;
  const text = date
    ? format(date, appearance === "field" ? "MM/dd/yyyy" : "MMM d")
    : `No ${label.toLowerCase()}`;

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <PropertyButton
          appearance={appearance}
          hideChevron
          aria-label={`${label}: ${text}${isOverdue ? ", overdue" : ""}. Change ${label.toLowerCase()}`}
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen((open) => !open);
          }}
        >
          {children ?? (
            <DateContent isCompact={textSize === "sm"}>
              <Box
                // The icon changes shape as well as color, so an overdue date doesn't rely on color alone.
                as={isOverdue ? WarningIcon : MeetingIcon}
                boxSize={
                  appearance === "field" ? 5 : textSize === "sm" ? 3.5 : 4
                }
                color={
                  isOverdue
                    ? textSize === "sm"
                      ? "icon.danger"
                      : "danger.base"
                    : "text.tertiary"
                }
                // A button colors every svg inside it, so on a card or row the red goes on fill to match the date beside it.
                fill={
                  isOverdue && textSize === "sm" ? "icon.danger" : undefined
                }
                // The warning triangle sits low in its box, so it is lifted to center on the date.
                position={
                  isOverdue && textSize === "sm" ? "relative" : undefined
                }
                top={isOverdue && textSize === "sm" ? "-1px" : undefined}
              />
              <Text
                isTruncated
                size={textSize}
                color={
                  isOverdue
                    ? "text.danger"
                    : !date
                      ? "text.tertiary"
                      : appearance === "field"
                        ? undefined
                        : "text.secondary"
                }
              >
                {text}
              </Text>
            </DateContent>
          )}
        </PropertyButton>
      </PopoverTrigger>
      {/* Out of the row or card, whose stacking context would put the calendar under the rows below it. */}
      <Portal>
        <PopoverContent p={2} width="fit-content">
          <Calendar
            value={date}
            minDate={minDate}
            maxDate={maxDate}
            isClearable
            onChange={(picked) => {
              setIsOpen(false);
              if (!(picked && date && isSameDay(picked, date)))
                onChange(picked);
            }}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

type DateFieldProps = {
  /** The form field that holds the date, as an ISO instant or "" for none. */
  name: string;
  label: string;
  /** Which instant of the picked day is stored: its start for a start date, its end for a due date. */
  dayEdge: "start" | "end";
  minDate?: Date;
  maxDate?: Date;
};

/** An optional date in a form. */
export const DateField = ({
  name,
  label,
  dayEdge,
  minDate,
  maxDate,
}: DateFieldProps) => (
  <Controller
    name={name}
    render={({ field, fieldState: { error } }) => (
      <FormField label={label} isOptional error={error?.message}>
        <DateTimeSelect
          hideTime
          isClearable
          inputWidth="100%"
          placeholder={`No ${label.toLowerCase()}`}
          isInvalid={Boolean(error)}
          minDate={minDate}
          maxDate={maxDate}
          value={field.value ? new Date(field.value) : undefined}
          onChange={(date) =>
            field.onChange(
              !date
                ? ""
                : (dayEdge === "start" ? startOfDayIso : endOfDayIso)(date),
            )
          }
        />
      </FormField>
    )}
  />
);

type TagsEditorProps = {
  task: Pick<AmpTaskRowFragment, "id" | "tags">;
  /**
   * "full" lists removable chips, for the task page. "compact" is for rows
   * and cards: plain chips, capped at `maxTags`, and an icon to add one.
   */
  variant?: "full" | "compact";
  /** Compact only: how many chips show before the rest become a count. */
  maxTags?: number;
};

export const TagsEditor = ({
  task,
  variant = "full",
  maxTags,
}: TagsEditorProps) => {
  const { tasks } = useBoard();
  const attached = task.tags.flatMap((taskTag) =>
    taskTag.tag ? [taskTag.tag] : [],
  );

  return (
    <TagList
      attached={attached}
      variant={variant}
      maxTags={maxTags}
      onAdd={(name) => tasks.addTag(task.id, name)}
      onRemove={(tag) => tasks.removeTag(task.id, tag.id)}
    />
  );
};

type ListedTag = { id: string; name: string; color: string | null };

type TagListProps = Omit<TagsEditorProps, "task"> & {
  attached: ListedTag[];
  /** Takes an existing tag's name or a new one, which creates the tag. */
  onAdd: (name: string) => void;
  onRemove: (tag: ListedTag) => void;
};

/** Tag chips, and a picker that adds a workspace tag or creates one. */
export const TagList = ({
  attached,
  variant = "full",
  maxTags,
  onAdd,
  onRemove,
}: TagListProps) => {
  const { tags } = useBoard();
  const isCompact = variant === "compact";

  const shown =
    isCompact && maxTags !== undefined ? attached.slice(0, maxTags) : attached;
  const attachedIds = new Set(attached.map((t) => t.id));
  const options = tags.filter((option) => !attachedIds.has(option.id));

  return (
    <Row
      gap={1}
      flexWrap={isCompact && maxTags !== undefined ? "nowrap" : "wrap"}
      align="center"
      minWidth={0}
    >
      {shown.map((attachedTag) => (
        <TagChip
          key={attachedTag.id}
          name={attachedTag.name}
          color={attachedTag.color}
          onRemove={isCompact ? undefined : () => onRemove(attachedTag)}
          // A tag the server hasn't made yet has no id to remove it by.
          isRemoveDisabled={isPendingId(attachedTag.id)}
        />
      ))}
      {attached.length > shown.length && (
        <Text size="sm" color="text.tertiary">
          +{attached.length - shown.length}
        </Text>
      )}
      <Box
        display="flex"
        flexShrink={0}
        // The full editor's add button sits at the end of the row; compact rows leave it beside the chips.
        ml={isCompact ? undefined : "auto"}
        // Compact rows keep the add button's space but show it only on hover or keyboard focus.
        opacity={isCompact ? 0 : 1}
        _groupHover={{ opacity: 1 }}
        sx={{ "&:has(:focus-visible)": { opacity: 1 } }}
      >
        <PropertyPicker
          aria-label="Add a tag"
          appearance="ghost"
          options={options}
          value={undefined}
          optionValue={(option) => option.name}
          optionLabel={(option) => option.name}
          optionAccessories={(option) => ({
            leftAccessory: {
              type: "icon",
              icon: colorDotIcon(resolveBoardColor(option.color, "gray.500")),
            },
          })}
          supportsCreatableOptions
          placeholder="Add or create a tag…"
          onCreateOption={onAdd}
          onChange={(name) => {
            if (name) onAdd(name);
          }}
        >
          <Box as={TagIcon} boxSize={4} color="text.tertiary" />
          {!isCompact && attached.length === 0 && (
            <Text color="text.tertiary">Add tag</Text>
          )}
        </PropertyPicker>
      </Box>
    </Row>
  );
};

type TagChipProps = {
  name: string;
  color: string | null;
  onRemove?: () => void;
  isRemoveDisabled?: boolean;
};

const TagChip = ({
  name,
  color,
  onRemove,
  isRemoveDisabled = false,
}: TagChipProps) => (
  <Row
    align="center"
    gap={1.5}
    height={6}
    pl={2}
    pr={onRemove ? 0.5 : 2}
    borderRadius="full"
    borderWidth="hairline"
    borderColor="base.border"
    maxWidth={40}
  >
    <ColorDot color={resolveBoardColor(color, "gray.500")} />
    <TextWithTooltip size="sm">{name}</TextWithTooltip>
    {onRemove && (
      <IconButton
        aria-label={`Remove tag ${name}`}
        icon={CloseIcon}
        size="xs"
        variant="tertiary"
        isDisabled={isRemoveDisabled}
        onClick={onRemove}
      />
    )}
  </Row>
);
